/**
 * useFormPersistence — Persists form data to localStorage to survive page refreshes.
 */
import { useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { UseFormReturn } from 'react-hook-form';

export function useFormPersistence<T extends Record<string, any>>(
  formMethods: UseFormReturn<T>,
  formId: string,
  isOpen: boolean,
  onOpenChange: (open: boolean) => void
) {
  const { getItem, setItem } = useLocalStorage<T | null>(formId, null);

  // Load saved form data ONLY on mount (not on isOpen changes)
  useEffect(() => {
    const savedData = getItem();
    
    if (savedData) {
      // Check if we have a saved form state and if the dialog should be reopened
      const shouldReopen = localStorage.getItem(`${formId}_open`) === 'true';
      
      // Restore form values
      formMethods.reset(savedData);
      
      // Reopen the dialog if it was open when user navigated away
      if (shouldReopen) {
        onOpenChange(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save form data when values change
  useEffect(() => {
    const subscription = formMethods.watch((formValues) => {
      if (formValues && Object.keys(formValues).length > 0) {
        setItem(formId, formValues as T);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [formMethods, setItem, formId]);

  // Save dialog open state
  useEffect(() => {
    localStorage.setItem(`${formId}_open`, isOpen.toString());
    
    // Clear form data when dialog is closed
    if (!isOpen) {
      localStorage.removeItem(formId);
      localStorage.removeItem(`${formId}_open`);
    }
  }, [formId, isOpen]);

  const clearPersistedForm = () => {
    localStorage.removeItem(formId);
    localStorage.removeItem(`${formId}_open`);
  };

  return { clearPersistedForm };
}
